import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useLocation } from "wouter";

export type UserRole = "ADMIN" | "RECRUITMENT_AGENT" | "CANDIDATE";

export interface AuthUser {
  id: number;
  email: string;
  role: UserRole;
  fullName: string | null;
  orgId: number | null;
  isVerified?: boolean;
  verificationLevel?: string;
}

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string, role?: UserRole) => Promise<AuthUser>;
  logout: () => Promise<void>;
  checkSession: () => Promise<AuthUser | null>;
  redirectToDashboard: () => void;
}

const ROLE_DASHBOARDS: Record<UserRole, string> = {
  ADMIN: "/mother",
  RECRUITMENT_AGENT: "/agent/dashboard",
  CANDIDATE: "/candidate/dashboard",
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });
  const [, setLocation] = useLocation();

  const checkSession = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/session", {
        credentials: "include",
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.user) {
          setAuthState({
            user: data.user,
            isLoading: false,
            isAuthenticated: true,
          });
          return data.user;
        }
      }
      
      setAuthState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
      return null;
    } catch (error) {
      console.error("Session check failed:", error);
      setAuthState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
      return null;
    }
  }, []);

  const login = useCallback(async (email: string, password: string, role?: UserRole) => {
    try {
      const endpoint = "/api/auth/login";
      const body = { email, password, role };
      
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Login failed");
      }

      const data = await res.json();
      
      // Build user object consistently
      const user = data.user || {
        id: data.id,
        email: data.email,
        role: "EMPLOYER" as UserRole,
        fullName: data.companyName,
        orgId: data.orgId || null,
        tier: data.tier,
      };
      
      setAuthState({
        user,
        isLoading: false,
        isAuthenticated: true,
      });

      const dashboard = ROLE_DASHBOARDS[user.role as UserRole] ?? "/agent/dashboard";
      setLocation(dashboard);
      
      return user;
    } catch (error: any) {
      throw error;
    }
  }, [setLocation]);

  const logout = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout failed:", error);
    }
    
    setAuthState({
      user: null,
      isLoading: false,
      isAuthenticated: false,
    });
    setLocation("/login");
  }, [setLocation]);

  const redirectToDashboard = useCallback(() => {
    if (authState.user) {
      const dashboard = ROLE_DASHBOARDS[authState.user.role];
      setLocation(dashboard);
    }
  }, [authState.user, setLocation]);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  return (
    <AuthContext.Provider value={{
      ...authState,
      login,
      logout,
      checkSession,
      redirectToDashboard,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function useRequireAuth(allowedRoles?: UserRole[]) {
  const auth = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!auth.isLoading) {
      if (!auth.isAuthenticated) {
        setLocation("/login");
        return;
      }

      if (allowedRoles && auth.user && !allowedRoles.includes(auth.user.role)) {
        const dashboard = ROLE_DASHBOARDS[auth.user.role] ?? "/agent/dashboard";
        setLocation(dashboard);
      }
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.user, allowedRoles, setLocation]);

  return auth;
}
